import { Injectable } from '@angular/core';
import { CryptoService } from './crypto.service';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  private db!: IDBDatabase;
  private dbReady: Promise<void>;
  private indexedDbAvailable = false;
  private readonly fallbackPrefix = 'secure-storage:';
  private syncChannel: BroadcastChannel | null = null;
  private fallbackSyncPromises: Record<'client' | 'admin', Promise<void> | null> = {
    client: null,
    admin: null
  };

  constructor(private cryptoService: CryptoService) {
    this.dbReady = this.initDB();
    void this.purgeLegacyPasswordRecord().catch((error) => {
      console.warn('Failed to remove a legacy password record:', error);
    });

    // Monkey-patch localStorage.clear to preserve the active admin session metadata.
    try {
      const originalClear = localStorage.clear.bind(localStorage);
      localStorage.clear = () => {
        const adminID = localStorage.getItem('adminID');
        const adminPmLevel = localStorage.getItem('adminPmLevel');
        originalClear();
        if (adminID !== null) {
          localStorage.setItem('adminID', adminID);
        }
        if (adminPmLevel !== null) {
          localStorage.setItem('adminPmLevel', adminPmLevel);
        }
      };
    } catch (e) {
      console.warn('Failed to monkey-patch localStorage.clear', e);
    }

    if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
      try {
        this.syncChannel = new BroadcastChannel('secure-storage-sync');
        this.syncChannel.addEventListener('message', (event) => this.handleSyncMessage(event.data));
      } catch (e) {
        console.warn('Failed to initialize BroadcastChannel:', e);
      }
    }

    // Monkey-patch CookieService.prototype.deleteAll to preserve cookies starting with 'admin_'
    try {
      const originalDeleteAll = CookieService.prototype.deleteAll;
      CookieService.prototype.deleteAll = function (
        path?: string,
        domain?: string,
        secure?: boolean,
        sameSite?: 'Lax' | 'None' | 'Strict'
      ) {
        try {
          const allCookies = this.getAll();
          for (const key of Object.keys(allCookies)) {
            if (!key.startsWith('admin_')) {
              this.delete(key, path || '/', domain, secure, sameSite);
            }
          }
        } catch (e) {
          originalDeleteAll.call(this, path, domain, secure, sameSite);
        }
      };
    } catch (e) {
      console.warn('Failed to monkey-patch CookieService.prototype.deleteAll', e);
    }

    // Clear IndexedDB when tab/browser is closed
    // window.addEventListener('beforeunload', async () => {
    // await this.clearStorage();
    // });
  }

  /** Initialize IndexedDB and return a promise */
  private initDB(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof indexedDB === 'undefined') {
        console.warn('IndexedDB is not available. Falling back to session storage.');
        this.indexedDbAvailable = false;
        resolve();
        return;
      }

      const request = indexedDB.open('SecureStorageDB', 1);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys');
        }
        if (!db.objectStoreNames.contains('data')) {
          db.createObjectStore('data');
        }
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        this.indexedDbAvailable = true;
        resolve(); // Resolve the promise once the database is ready
      };

      request.onerror = (event: any) => {
        console.error('IndexedDB Error:', event.target.error);
        this.indexedDbAvailable = false;
        resolve();
      };
    });
  }

  /** Ensure IndexedDB is ready before performing any action */
  private async ensureDBReady(): Promise<void> {
    try {
      await this.dbReady;
    } catch (error) {
      this.indexedDbAvailable = false;
      console.warn('Secure storage falling back because IndexedDB init failed.', error);
    }

    if (!this.indexedDbAvailable) {
      const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
      const currentScope: 'client' | 'admin' = isAdminRoute ? 'admin' : 'client';
      if (!this.fallbackSyncPromises[currentScope]) {
        this.fallbackSyncPromises[currentScope] = this.syncFallbackStorage(currentScope);
      }
      await this.fallbackSyncPromises[currentScope];
    }
  }

  private handleSyncMessage(data: any): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    if (data.type === 'REQUEST_SESSION') {
      const fallbackStorage = this.getFallbackStorage();
      if (!fallbackStorage) {
        return;
      }

      // Verify user/admin context matches the identity stored in our own fallback storage
      const fallbackKey = this.getFallbackKey(data.scope === 'admin' ? 'adminID' : 'userID');
      const myId = fallbackStorage.getItem(fallbackKey);
      if (!myId || myId !== String(data.identifier)) {
        return;
      }

      const payload: Record<string, string | null> = {};
      let hasKeys = false;
      try {
        for (let i = 0; i < fallbackStorage.length; i++) {
          const key = fallbackStorage.key(i);
          if (key && key.startsWith(this.fallbackPrefix)) {
            const suffix = key.substring(this.fallbackPrefix.length);
            const isKeyAdmin = suffix.startsWith('admin');
            const targetIsAdmin = data.scope === 'admin';

            if (isKeyAdmin === targetIsAdmin) {
              payload[key] = fallbackStorage.getItem(key);
              const isMetadataKey = suffix === 'userID' || suffix === 'adminID' || suffix === 'secretKeyReady' || suffix === 'adminSecretKeyReady';
              if (!isMetadataKey) {
                hasKeys = true;
              }
            }
          }
        }
      } catch (e) {
        console.warn('Failed to collect fallback storage keys for sync:', e);
      }

      if (hasKeys && this.syncChannel) {
        this.syncChannel.postMessage({
          type: 'SESSION_RESPONSE',
          scope: data.scope,
          identifier: data.identifier,
          payload
        });
      }
    }
  }

  private syncFallbackStorage(scope: 'client' | 'admin'): Promise<void> {
    return new Promise((resolve) => {
      const fallbackStorage = this.getFallbackStorage();
      if (!fallbackStorage) {
        resolve();
        return;
      }

      const isScopeAdmin = scope === 'admin';
      const expectedScope = scope;
      const expectedId = typeof localStorage !== 'undefined'
        ? localStorage.getItem(isScopeAdmin ? 'adminID' : 'userID')
        : null;

      let hasExistingKeys = false;
      try {
        for (let i = 0; i < fallbackStorage.length; i++) {
          const key = fallbackStorage.key(i);
          if (key && key.startsWith(this.fallbackPrefix)) {
            const suffix = key.substring(this.fallbackPrefix.length);
            const isKeyAdmin = suffix.startsWith('admin');
            if (isKeyAdmin === isScopeAdmin) {
              hasExistingKeys = true;
              break;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to inspect fallback storage keys:', e);
      }

      if (hasExistingKeys) {
        resolve();
        return;
      }

      if (!this.syncChannel || !expectedId) {
        resolve();
        return;
      }

      let resolved = false;

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve();
        }
      }, 150);

      const messageHandler = (event: MessageEvent) => {
        const msgData = event.data;
        if (
          msgData &&
          msgData.type === 'SESSION_RESPONSE' &&
          msgData.scope === expectedScope &&
          String(msgData.identifier) === String(expectedId) &&
          msgData.payload
        ) {
          try {
            Object.entries(msgData.payload).forEach(([key, val]) => {
              if (key.startsWith(this.fallbackPrefix) && typeof val === 'string') {
                fallbackStorage.setItem(key, val);
              }
            });
          } catch (e) {
            console.error('Error writing synchronized fallback session data:', e);
          }
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve();
          }
        }
      };

      const cleanup = () => {
        if (this.syncChannel) {
          this.syncChannel.removeEventListener('message', messageHandler);
        }
      };

      this.syncChannel.addEventListener('message', messageHandler);
      this.syncChannel.postMessage({
        type: 'REQUEST_SESSION',
        scope: expectedScope,
        identifier: expectedId
      });
    });
  }

  private getFallbackStorage(): Storage | null {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage;
    }

    if (typeof localStorage !== 'undefined') {
      return localStorage;
    }

    return null;
  }

  private getFallbackKey(key: string): string {
    return `${this.fallbackPrefix}${key}`;
  }

  private translateKey(key: string): string {
    if (key === 'permissions') {
      const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
      const hasAdminId = typeof localStorage !== 'undefined' && !!localStorage.getItem('adminID');
      if (isAdminRoute && hasAdminId) {
        return 'admin_permissions';
      }
    }
    return key;
  }

  /** Store encryption key in IndexedDB */
  async setSecretKey(userId: any, email: any, isAdmin: boolean = false): Promise<void> {
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      const fallbackStorage = this.getFallbackStorage();
      if (fallbackStorage) {
        fallbackStorage.setItem(this.getFallbackKey(isAdmin ? 'adminSecretKeyReady' : 'secretKeyReady'), 'true');
        fallbackStorage.setItem(this.getFallbackKey(isAdmin ? 'adminID' : 'userID'), String(userId));
      }
      return;
    }

    const secretPassword = btoa(btoa(userId) + btoa(email));
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const encryptionKey = await this.cryptoService.deriveKey(secretPassword, new TextDecoder().decode(salt));

    const dbKey = isAdmin ? 'adminEncryptionKey' : 'encryptionKey';
    await this.storeKeyInDB(dbKey, encryptionKey);
  }

  /** Store key in IndexedDB */
  private async storeKeyInDB(key: string, value: any): Promise<void> {
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      return;
    }

    const transaction = this.db.transaction('keys', 'readwrite');
    const store = transaction.objectStore('keys');
    store.put(value, key);
  }

  /** Retrieve key from IndexedDB */
  async getSecretKey(dbKey: string = 'encryptionKey'): Promise<CryptoKey | null> {
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('keys', 'readonly');
      const store = transaction.objectStore('keys');
      const request = store.get(dbKey);

      request.onsuccess = (event: any) => {
        resolve(event.target.result || null);
      };

      request.onerror = (event: any) => {
        reject(event.target.error);
      };
    });
  }

  /** Store encrypted data in IndexedDB */
  async setItem(key: string, value: string): Promise<void> {
    const translatedKey = this.translateKey(key);
    await this.ensureDBReady();

    const isAdmin = translatedKey.startsWith('admin_');

    if (!this.indexedDbAvailable) {
      const fallbackStorage = this.getFallbackStorage();
      if (fallbackStorage) {
        fallbackStorage.setItem(this.getFallbackKey(translatedKey), value);

        // Keep the fallback identity marker in sync with localStorage during impersonation/routing changes
        if (typeof localStorage !== 'undefined') {
          const activeUserId = localStorage.getItem('userID');
          if (activeUserId !== null) {
            fallbackStorage.setItem(this.getFallbackKey('userID'), activeUserId);
          }
          const activeAdminId = localStorage.getItem('adminID');
          if (activeAdminId !== null) {
            fallbackStorage.setItem(this.getFallbackKey('adminID'), activeAdminId);
          }
        }
      }
      return;
    }

    const dbKey = isAdmin ? 'adminEncryptionKey' : 'encryptionKey';
    const encryptionKey = await this.getSecretKey(dbKey);
    if (!encryptionKey) {
      console.info('Encryption key not available for key: ' + translatedKey);
      return;
    }

    const encryptedValue = await this.cryptoService.encrypt(value, encryptionKey);
    const transaction = this.db.transaction('data', 'readwrite');
    const store = transaction.objectStore('data');
    store.put(encryptedValue, translatedKey);
  }

  /** Retrieve and decrypt data from IndexedDB */
  async getItem(key: string): Promise<string | null> {
    const translatedKey = this.translateKey(key);
    await this.ensureDBReady();

    const isAdmin = translatedKey.startsWith('admin_');

    if (!this.indexedDbAvailable) {
      const fallbackStorage = this.getFallbackStorage();
      return fallbackStorage
        ? fallbackStorage.getItem(this.getFallbackKey(translatedKey))
        : null;
    }

    const dbKey = isAdmin ? 'adminEncryptionKey' : 'encryptionKey';
    const encryptionKey = await this.getSecretKey(dbKey);
    if (!encryptionKey) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('data', 'readonly');
      const store = transaction.objectStore('data');
      const request = store.get(translatedKey);

      request.onsuccess = async (event: any) => {
        const encryptedValue = event.target.result;
        if (encryptedValue) {
          try {
            const decryptedValue = await this.cryptoService.decrypt(encryptedValue, encryptionKey);
            resolve(decryptedValue);
          }
          catch (error) {
            console.log('Decryption error:', error);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = (event: any) => {
        reject(event.target.error);
      };
    });
  }

  /** Remove one stored value without clearing the rest of the active session. */
  async removeItem(key: string): Promise<void> {
    const translatedKey = this.translateKey(key);
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      this.getFallbackStorage()?.removeItem(this.getFallbackKey(translatedKey));
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('data', 'readwrite');
      transaction.objectStore('data').delete(translatedKey);
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event);
    });
  }

  /**
   * Remove the password record written by portal versions released before the
   * MFA reauthentication flow stopped persisting reusable credentials.
   */
  async purgeLegacyPasswordRecord(): Promise<void> {
    const legacyPasswordKey = globalThis.atob('ZGZlNWVlODlmZGpkMzMzZGY=');
    await this.removeItem(legacyPasswordKey);
  }

  /** Remove key on logout */
  async clearEncryptionKey(isAdmin: boolean = false): Promise<void> {
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      const fallbackStorage = this.getFallbackStorage();
      fallbackStorage?.removeItem(this.getFallbackKey(isAdmin ? 'adminSecretKeyReady' : 'secretKeyReady'));
      return;
    }

    const transaction = this.db.transaction('keys', 'readwrite');
    const store = transaction.objectStore('keys');
    store.delete(isAdmin ? 'adminEncryptionKey' : 'encryptionKey');
  }

  /** Clear all stored data */
  async clear(): Promise<void> {
    await this.clearStorage(false);
  }

  async clearStorage(isAdmin: boolean = false): Promise<void> {
    await this.ensureDBReady();

    if (!this.indexedDbAvailable) {
      const fallbackStorage = this.getFallbackStorage();
      if (!fallbackStorage) {
        return;
      }

      Object.keys(fallbackStorage)
        .filter((storageKey) => storageKey.startsWith(this.fallbackPrefix))
        .forEach((storageKey) => {
          const isKeyAdmin = storageKey.startsWith(this.fallbackPrefix + 'admin_');
          if (isAdmin === isKeyAdmin) {
            fallbackStorage.removeItem(storageKey);
          }
        });
      return;
    }
  
    // Clear the specific encryption key and matching data keys
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keys', 'data'], 'readwrite');
      const keysStore = transaction.objectStore('keys');
      const dataStore = transaction.objectStore('data');
  
      // Clear specific encryption key
      const keyToDelete = isAdmin ? 'adminEncryptionKey' : 'encryptionKey';
      keysStore.delete(keyToDelete);
  
      // Selectively clear stored data
      const request = dataStore.openCursor();
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key as string;
          const isKeyAdmin = key.startsWith('admin_');
          if (isAdmin === isKeyAdmin) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
  
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event);
    });
  }
}
